import React, {useState, useCallback} from 'react';
import {ReactFlowInstance} from 'reactflow';

import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import CloneProcessTaskSchemaModal from 'processes/settings/CloneProcessTaskSchemaModal';
import * as ProcessTaskSchemaUtils from 'processes/settings/ProcessTaskSchemaUtils';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {isNodeDataDuplicable} from '../adapterUtils';
import {isInteractiveTaskNode} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartNodeChange} from '../processFlowchartConfigEditorUtils';

export interface UseNodeDuplicationParams {
  locked: boolean;
  reactFlowInstance?: ReactFlowInstance;
  onReactFlowNodeChangesOnly: (nodeChanges: Array<FlowchartNodeChange>) => void;
}
export interface UseNodeDuplicationReturn {
  onNodeDuplicate: (nodeId: string) => void;
  nodeDuplicationModalJsx: JSX.Element | null;
}

export interface NodeToDuplicate {
  taskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  taskDefinitionVersionId: string;
}

export function useNodeDuplication({
  locked,
  reactFlowInstance,
  onReactFlowNodeChangesOnly,
}: UseNodeDuplicationParams): UseNodeDuplicationReturn {
  const [nodeToDuplicate, setNodeToDuplicate] = useState<NodeToDuplicate | null>(null);
  const onNodeDuplicate = useCallback(
    async (nodeId: string): Promise<void> => {
      if (locked) {
        return;
      }
      const node = reactFlowInstance?.getNode(nodeId);
      if (node == null || !isInteractiveTaskNode(node) || !isNodeDataDuplicable(node.data)) {
        return;
      }
      const taskDefinitionVersionId = node.data.taskDefinitionVersionSpec.currentVersionId;
      if (taskDefinitionVersionId == null) {
        return;
      }

      const taskDefinitionVersion =
        await ProcessTaskSchemaUtils.fetchTaskDefinitionVersion(taskDefinitionVersionId);
      if (taskDefinitionVersion == null || isInaccessible(taskDefinitionVersion.processTaskSchema)) {
        return;
      }
      setNodeToDuplicate({taskSchema: taskDefinitionVersion.processTaskSchema, taskDefinitionVersionId});
    },
    [locked, reactFlowInstance]
  );

  const nodeDuplicationModalJsx = nodeToDuplicate ? (
    <CloneProcessTaskSchemaModal
      originalProcessTaskSchema={nodeToDuplicate.taskSchema}
      actionOrigin={ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS}
      actionMethod={ActionMethod.BUTTON_CLICK}
      onCloneSuccess={(clonedSchema) => {
        // TODO: create and add task node to flowchart
      }}
      onRequestClose={() => {
        setNodeToDuplicate(null);
      }}
    />
  ) : null;

  return {onNodeDuplicate, nodeDuplicationModalJsx};
}
